import { Component, OnInit, HostListener, ViewChild, ChangeDetectorRef } from '@angular/core';
import { AdminReportService } from 'app/core/admin-report.service';
import { GetService } from 'app/core/get.service';
import { saveAs as importedSaveAs } from 'file-saver';
import { DatePipe } from '@angular/common';
import { NotificationsService } from 'angular2-notifications';
import { AppMemoryService } from 'app/core/app-memory.service';
import { TranslateService } from '@ngx-translate/core';
import { TooltipComponent } from '../../core/tooltip/tooltip.component';
import 'rxjs/add/operator/takeWhile';
import {environment} from '../../../environments/environment';

interface FilterOptionType {
  label: string;
  value: string | number;
}

interface SpecificValuesType {
  [key: string]: (string | number)[];
}

interface ActiveFiltersType {
  [key: string]: number;
}

@Component({
  selector: 'app-admin-report',
  templateUrl: './admin-report.component.html',
  styleUrls: ['./admin-report.component.less'],
  providers: [DatePipe]
})
export class AdminReportComponent implements OnInit {
  public filtersKeys: String | any[];
  public filterOptions: { [key: string]: FilterOptionType[] };
  public drugs: any[] = [] as any[];
  public horses: any[] = [] as any[];
  public reportsKeys: any[] = [] as any[];
  public reports: any[] = [] as any[];
  public activeFilters: ActiveFiltersType = {} as ActiveFiltersType;
  public specificValues: SpecificValuesType = {} as SpecificValuesType;
  public currentWidth: (number | undefined);
  public period: { type: string, [key: string]: string } = {} as { type: string, [key: string]: string };
  public periodPayload = {};
  public periodSelected = false;
  public emailSent = false;
  public emailSending = false;
  public exporting = false;
  public showDateRange = false;
  public step = 10;
  public limit: { [key: string]: number } = {} as { [key: string]: number };
  public offset: { [key: string]: number } = {} as { [key: string]: number };
  public sorting: { [key: string]: { field: string, order: string } } = {} as { [key: string]: { field: string, order: string } };
  public serverError = null;
  public dateRangeRequired = false;
  public dateRangeErrorMessage: {from?: string, due?: string, date?: string} = {} as {from?: string, due?: string, date?: string};
  public isShowTooltip: boolean = false;

  public datePickerPeriodFrom = {
    minDate: new Date(new Date().setFullYear(new Date().getFullYear() - 3)),
    maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 3)),
    currentDate: new Date() as Date,
    currentDate_input: ''/*this.datePipe.transform(new Date(), environment.dateFormat)*/,
    isActive: false as boolean
  };

  public datePickerPeriodTo = {
    minDate: new Date(new Date().setFullYear(new Date().getFullYear() - 3)),
    maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 3)),
    currentDate: new Date() as Date,
    currentDate_input: ''/*this.datePipe.transform(new Date(), environment.dateFormat)*/,
    selectedDate: '' as String,
    isActive: false as boolean
  };

  public datePickerMask = {
    guide: true,
    showMask: true,
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
  };

  public translation = {};
  public alive: boolean = true;
  @ViewChild('addReportTutorial') tooltip: TooltipComponent;

  constructor(
    private adminReportService: AdminReportService,
    private getService: GetService,
    private datePipe: DatePipe,
    private notificationsService: NotificationsService,
    public appMemory: AppMemoryService,
    public translateService: TranslateService,
    public changeDetectorRef: ChangeDetectorRef) {
      this.getTranslation();

     }

  ngOnInit(): void {
    this.getService.getStatic().then(res => this.drugs = res.drugs);
    this.getService.getAllHorses().then(res => this.horses = res);
    this.adminReportService.getReportFilters().then(res => this.restructureFilterOptions(res));
    this.resizedWindow(window);
  }

  getTooltip() {
    return this.isShowTooltip = this.appMemory.getTooltip();
  }

  /*********************** REQUESTS ***********************/

  /**
   * Download the report in PDF or CSV
   * @param {('csv'|'pdf')} type
   * @returns {Promise<void>}
   */
  downloadReport(type: 'csv' | 'pdf'): Promise<void> {
    this.exporting = true;
    const criteria: FormData = this.formPayload();
    const payload: FormData = this.objectToFormData({ file_type: type }, criteria);

    return this.adminReportService.downloadReport(payload).then(res => {
      this.exporting = false;
      let file: Blob;
      let fileType: String;
      if (type === 'csv') {
        file = new Blob([res], { type: 'text/csv;charset=utf-8' });
        fileType = 'csv';
      } else {
        file = res;
        fileType = 'pdf';
      }
      importedSaveAs(file, `report.${fileType}`);
    }, err => {
      this.exporting = false;
    });
  }

  /**
   * Send on email the report
   * @returns {Promise<void>}
   */
  sendOnEmail(): Promise<void> {
    this.emailSending = true;
    const payload: FormData = this.formPayload();
    return this.adminReportService.sendMail(payload).then(res => {
      this.emailSent = true;
      this.emailSending = false;
      this.notificationsService.success(
        `${this.translation['sendEmailReport']}`,
        `${this.translation['sentEmailReport']}`
      );
    }, err => {
      this.emailSending = false;
      this.notificationsService.error(
        `${this.translation['sendEmailReport']}`,
        `${this.translation['errorOccurred']}`
      );
    });
  }

  /**
   * Get the reports according to current filter
   */
  getReports(sort?: boolean): void {
    this.dateRangeRequired = false;
    if (!this.period.type && !(this.period.start && this.period.end)) {
      this.notificationsService.error(
        `${this.translation['dateRange']}`,
        `${this.translation['dateRange']} ${this.translation['isRequired']}`
      );
      this.dateRangeRequired = true;
      return;
    }
    const payload: FormData = this.formPayload(sort);
    this.emailSent = false;
    this.adminReportService.postAdministrativeReports(payload).then(res => this.restructureReports(res, sort));
  }

  /*********************** UTILITIES ***********************/

  /**
   * Organize the filter response
   * @param {(Object | any)} options
   */
  restructureFilterOptions(options: Object | any): void {
    this.filtersKeys = Object.keys(options);
    this.filterOptions = options;
  }

  /**
   * Organize the response
   * @param {(Object | any)} reports
   */
  restructureReports(reports: Object | any, sort?: boolean): void {
    this.reportsKeys = Object.keys(reports);

    for (let i = 0; i < this.reportsKeys.length; i++) {
      const key = this.reportsKeys[i];

      if (!key.includes('_count') && (this.activeFilters[`in_${key.slice(0, -1)}`] || !this.activeFilters.length) && !sort) {
        if (this.reports[key] && this.reports[key].length && this.offset[key.slice(0, -1)]) {
          this.reports[key] = this.reports[key].concat(reports[key]);
        } else {
          this.reports[key] = reports[key];
        }
      } else {
        this.reports[key] = reports[key];
      }
    }
  }

  /**
   * Transforms object to FormData
   * @param {Object} obj
   * @param {FormData} [form]
   * @param {((String | Number))} [namespace]
   * @returns {FormData}
   */
  objectToFormData(object: Object, form?: FormData, namespace?: string): FormData {
    const formData = form || new FormData();
    for (const property in object) {
      if (!object.hasOwnProperty(property) || !object[property]) {
        continue;
      }
      const formKey = namespace ? `${namespace}[${property}]` : property;
      if (object[property] instanceof Date) {
        formData.append(formKey, object[property].toISOString());
      } else if (typeof object[property] === 'object' && !(object[property] instanceof File)) {
        this.objectToFormData(object[property], formData, formKey);
      } else {
        formData.append(formKey, object[property]);
      }
    }
    return formData;
  }

  /**
   * Gather all data for payload and prepare for request
   * @returns {FormData}
   */
  formPayload(sort?: boolean): FormData {
    const tempSpecific = Object.assign({}, this.specificValues);
    if (this.activeFilters['in_horses'] === 1) {
      tempSpecific['horses_id'] = this.specificValues['horses_id'];
    } else {
      tempSpecific['horses_id'] = [];
    }

    let page: any = { limit: this.limit, offset: this.offset };
    if (sort) {
      page = {
        limit: this.offset,
        offset: 0
      };
    }

    // const sorting = { 'note': { 'order':  'asc', 'field': 'type' } };
    const payload: Object = Object.assign({},
      this.activeFilters,
      tempSpecific,
      { period: this.periodPayload },
      { limit: page.limit },
      { offset: page.offset },
      { sorting: this.sorting });
    return this.objectToFormData(payload);
  }

  checkDataLength(data, keys): boolean {
    for (let i = 0; i < keys.length; i++) {
      const element = keys[i];
      // console.log(element, data[element]);
      if (!element.includes('_count')) {
        if (data[element].length) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get and parse registration date of the user
   * @returns {Date}
   */
  getRegistrationDate(): Date{
    return new Date(this.appMemory.user.created_at);
  }

  /**
   * Check validity of period and disabled "Pick date" button
   * @returns {boolean}
   */
  checkPeriodValidity(): boolean {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' };
    if (this.period.type !== undefined) {
      if (this.period.type.length) {
        return false;
      } else {
        if (this.period.start && this.period.end && this.period.start > this.period.end) {
          this.dateRangeErrorMessage = {
            from: 'OTHER.periodFromGreaterPeriodDue',
            due: 'OTHER.periodFromGreaterPeriodDue'
          };
        }
        // else if (new Date(this.period.start).getTime() < new Date(this.appMemory.user.created_at).getTime()) {
        //   this.dateRangeErrorMessage = {
        //     from: `OTHER.dateExceededReg`,
        //     date: new Date(this.appMemory.user.created_at).toLocaleString('en-GB', options)
        //   };
        // } else if (new Date(this.period.end).getTime() < new Date(this.appMemory.user.created_at).getTime()) {
        //   this.dateRangeErrorMessage = {
        //     due: `OTHER.dateExceededReg`,
        //     date: new Date(this.appMemory.user.created_at).toLocaleString('en-GB', options)
        //   };
        // }
        else {
          this.dateRangeErrorMessage = {};
        }

        return !(this.period.start <= this.period.end);
      }
    }
  }

  /*********************** OTHER ***********************/

  /**
   * Activate a filter
   * @param {boolean} event
   * @param {string} key
   */
  onChecked(event: boolean, key: string): void {
    this.activeFilters[`in_${key}`] = +event;
    this.offset = {};
    this.emailSent = false;
  }

  /**
   *  Get specific selected values from fitlers
   * @param {(string|number)[]} event
   * @param {(string|number)} key
   * @param {string} [suffix]
   */
  onSelect(event: (string | number)[], key: (string | number), suffix?: string): void {
    this.specificValues[key + suffix] = event;
    this.offset = {};
    this.emailSent = false;
  }

    formatDate(date) {
        if (date) {
            const arr = date.split('/');
            switch (environment.dateFormat) {
                case 'MM/dd/yyyy':
                    return arr[0] + '/' + arr[1] + '/' + arr[2];
                case 'dd/MM/yyyy':
                default:
                    return arr[1] + '/' + arr[0] + '/' + arr[2];
            }
        }
    }

  /**
 * Set selected date from dateicker
 * @param {({ selectedDate: (Date | String), currentDate: (Date | String) })} destination
 * @param {*} payloadProperty
 * @param {Date} date
 */
  selectDate(destination: { selectedDate: (Date | String), currentDate: (Date | String) }, payloadProperty, date: Date): void {
    if (this.serverError) {
      this.serverError[payloadProperty] = null;
    }
    if (destination && date) {
      destination.selectedDate = this.datePipe.transform(new Date(date), 'yyyy-MM-dd');
      destination.currentDate = date;
      this.period[payloadProperty] = destination.selectedDate.toString();
      if (payloadProperty === 'start') {
        this.datePickerPeriodFrom.currentDate_input = this.datePipe.transform(date, environment.dateFormat);
      } else {
        this.datePickerPeriodTo.currentDate_input = this.datePipe.transform(date, environment.dateFormat);
      }
      this.offset = {};
    }
  }

  setStartDateInput() {
    if (this.serverError) {
      this.serverError.start = null;
    }
    // (new Date(this.formatDate(this.datePickerPeriodFrom.currentDate_input))).getTime() > this.datePickerPeriodFrom.maxDate.getTime() ||
    // (new Date(this.formatDate(this.datePickerPeriodFrom.currentDate_input))).getTime() < this.datePickerPeriodFrom.minDate.getTime() ||
    if (
      (new Date(this.formatDate(this.datePickerPeriodFrom.currentDate_input))).toDateString() === 'Invalid Date'
    ) {
      if (this.serverError) {
        this.serverError.start = 'CORE.invalidDate'; ;
      } else {
        this.serverError = { start: 'CORE.invalidDate' };
      }
      return;
    }
    this.datePickerPeriodFrom.currentDate = new Date(this.formatDate(this.datePickerPeriodFrom.currentDate_input));
    this.period.start = this.datePipe.transform(this.formatDate(this.datePickerPeriodFrom.currentDate_input), 'yyyy-MM-dd');
    this.datePickerPeriodFrom.isActive = false;
    this.offset = {};
  }

  setEndDateInput() {
    if (this.serverError) {
      this.serverError.end = null;
    }
    // (new Date(this.formatDate(this.datePickerPeriodTo.currentDate_input))).getTime() > this.datePickerPeriodFrom.maxDate.getTime() ||
    // (new Date(this.formatDate(this.datePickerPeriodTo.currentDate_input))).getTime() < this.datePickerPeriodFrom.minDate.getTime() ||
    if (
      (new Date(this.formatDate(this.datePickerPeriodTo.currentDate_input))).toDateString() === 'Invalid Date'
    ) {
      if (this.serverError) {
        this.serverError.end = 'CORE.invalidDate';
      } else {
        this.serverError = { end: 'CORE.invalidDate' };
      }
      return;
    }
    this.datePickerPeriodTo.currentDate = new Date(this.formatDate(this.datePickerPeriodTo.currentDate_input));
    this.period.end = this.datePipe.transform(this.formatDate(this.datePickerPeriodTo.currentDate_input), 'yyyy-MM-dd');
    this.datePickerPeriodTo.isActive = false;
    this.offset = {};
  }

  /**
   * Confirm selected period from date range modal
   */
  confirmPeriod(): void {
    this.periodPayload = Object.assign({}, this.period);
    this.dateRangeRequired = false;
    this.periodSelected = true;
    this.emailSent = false;
  }

  @HostListener('window:resize', ['$event.currentTarget'])
  resizedWindow(event): void {
    this.currentWidth = event.innerWidth;
  }

  /**
   * Check if any filter is active
   * @returns {boolean}
   */
  checkActiveFilters(): boolean {
    if (Object.keys(this.activeFilters).length) {
      const values: number[] = Object.keys(this.activeFilters).map(key => this.activeFilters[key]);
      return values.some(el => el === 1);
    }
    return false;
  }

  /**
   * Load more data for pagination
   * @param {string} key
   */
  viewMore(key: string): void {
    this.offset[key.slice(0, -1)] = this.reports[key].length;
    this.limit[key.slice(0, -1)] = this.step;

    this.getReports();

    this.offset[key.slice(0, -1)] = this.reports[key].length + this.step;
  }

  sortData(key, event) {
    // console.log(key.slice(0, -1), event);
    this.sorting[key.slice(0, -1)] = event;
    this.getReports(true);
    // console.log(this.sorting);
  }

  getTranslation() {
    this.translateService.get('REPORT.sendEmailReport').subscribe((word: string) => {
      this.translation['sendEmailReport'] = word;
    });
    this.translateService.get('REPORT.sentEmailReport').subscribe((word: string) => {
      this.translation['sentEmailReport'] = word;
    });
    this.translateService.get('OTHER.errorOccurred').subscribe((word: string) => {
      this.translation['errorOccurred'] = word;
    });
    this.translateService.get('REPORT.dateRange').subscribe((word: string) => {
      this.translation['dateRange'] = word;
    });
    this.translateService.get('CORE.isRequired').subscribe((word: string) => {
      this.translation['isRequired'] = word;
    });
  }

  showTooltip(key, localClass) {
    return this.tooltip.showTooltip(key, localClass);
  }
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/emr-webapp/src/app/custom/admin-report/admin-report.component.ts